import { useMemo } from "react";
import { UseQueryResult } from "@tanstack/react-query";
import { useStoryblokProxy } from "./useStoryblokProxy";
import { UseFetchOptions } from "../types/StoryblokTypes";
import { NewsStory } from "../types/NewsTypes";
import { AxiosHeaders } from "axios";
import { transformStoryblokToNews } from "../utils/storyblokTransforms";

interface NewsResponse {
  storyData: NewsStory[];
  headers: AxiosHeaders;
}

export const useNews = (
  args: UseFetchOptions,
  abortRequest?: boolean
): UseQueryResult<NewsResponse> => {
  const storyblokData = useStoryblokProxy(args, abortRequest);

  const transformedData = useMemo<NewsStory[] | undefined>(() => {
    if (!storyblokData.data) return undefined;

    const { stories = [], story, rels = [] } = storyblokData.data;

    // Handle single story case (when slug is provided)
    if (story) {
      return transformStoryblokToNews([story], rels);
    }

    // Handle multiple stories case (when no slug is provided)
    return transformStoryblokToNews(stories, rels);
  }, [storyblokData.data]);

  return {
    ...storyblokData,
    data: {
      storyData: transformedData,
      headers: storyblokData.data?.headers,
    },
  } as unknown as UseQueryResult<NewsResponse>;
};
